// CodePanel component - shows simplified algorithm code
import type { SortAlgorithm } from '@/core/types';
import { Colors } from '@/styles/colors';

interface CodePanelProps {
  algorithm: SortAlgorithm;
}

export function CodePanel({ algorithm }: CodePanelProps) {
  const getSimplifiedCode = (name: string): string => {
    switch (name) {
      case 'Bubble Sort':
        return `for i from 0 to n-2:
    swapped = false
    for j from 0 to n-i-2:
        if arr[j] > arr[j+1]:
            swap(arr[j], arr[j+1])
            swapped = true
    if not swapped: break`;

      case 'Selection Sort':
        return `for i from 0 to n-2:
    minIndex = i
    for j from i+1 to n-1:
        if arr[j] < arr[minIndex]:
            minIndex = j
    if minIndex != i:
        swap(arr[i], arr[minIndex])`;

      case 'Insertion Sort':
        return `for i from 1 to n-1:
    key = arr[i]
    j = i-1
    while j >= 0 and arr[j] > key:
        arr[j+1] = arr[j]
        j--
    arr[j+1] = key`;

      case 'Merge Sort':
        return `mergeSort(arr, left, right):
    if left < right:
        mid = (left + right) / 2
        mergeSort(arr, left, mid)
        mergeSort(arr, mid+1, right)
        merge(arr, left, mid, right)`;

      case 'Quick Sort':
        return `quickSort(arr, low, high):
    if low < high:
        pi = partition(arr, low, high)
        quickSort(arr, low, pi-1)
        quickSort(arr, pi+1, high)

partition(arr, low, high):
    pivot = arr[high]
    i = low - 1
    for j from low to high-1:
        if arr[j] <= pivot:
            i++, swap(arr[i], arr[j])
    swap(arr[i+1], arr[high])
    return i + 1`;

      case 'Heap Sort':
        return `buildMaxHeap(arr)
for end from n-1 down to 1:
    swap(arr[0], arr[end])
    heapify(arr, 0, end)

heapify(arr, root, size):
    compare root with children
    move largest value upward`;

      case 'Shell Sort':
        return `gap = largest starting gap
while gap > 0:
    for i from gap to n-1:
        insert arr[i] into its gap-sorted place
    reduce gap`;

      default:
        return '// Pseudocode belum tersedia untuk algoritma ini';
    }
  };

  const getColorForLine = (line: string): string => {
    if (line.includes('swap') || line.includes('assign')) return Colors.status.swapping;
    if (line.includes('compare') || line.includes('if')) return Colors.status.comparing;
    if (line.includes('return') || line.includes('break')) return Colors.status.selected;
    if (line.includes('function') || line.includes('for') || line.includes('while')) return Colors.light.primary;
    return Colors.light.text;
  };

  const getBestUse = (): string => {
    switch (algorithm.name) {
      case 'Bubble Sort':
        return 'belajar konsep dasar';
      case 'Selection Sort':
        return 'data kecil';
      case 'Insertion Sort':
        return 'data kecil atau hampir urut';
      case 'Merge Sort':
        return 'data besar yang butuh hasil stabil';
      case 'Heap Sort':
        return 'data besar dengan memori tambahan kecil';
      case 'Shell Sort':
        return 'variasi cepat dari Insertion Sort';
      default:
        return 'kebutuhan umum';
    }
  };

  return (
    <div className="code-panel" style={{ backgroundColor: Colors.light.surface, border: `1px solid ${Colors.light.border}`, borderRadius: '8px', padding: '20px', flex: '1', minWidth: 0 }}>
      <h2 style={{ margin: '0 0 20px 0', color: Colors.light.primary }}>Pseudocode sederhana</h2>

      <div style={{ backgroundColor: Colors.light.background, border: `1px solid ${Colors.light.border}`, borderRadius: '4px', padding: '15px', fontFamily: 'monospace', fontSize: '13px', color: Colors.light.text, overflowX: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px', paddingBottom: '10px', borderBottom: `1px solid ${Colors.light.border}` }}>
          <div style={{ width: '20px', height: '20px', backgroundColor: Colors.light.background, borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ color: Colors.light.textSecondary, fontSize: '11px' }}>1</span>
          </div>
          <div style={{ color: Colors.light.primary, fontWeight: 'bold' }}>{algorithm.name}</div>
        </div>

        {getSimplifiedCode(algorithm.name).split('\n').map((line, index) => (
          <div key={`${line}-${index}`} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '4px' }}>
            <div style={{ width: '20px', color: Colors.light.textSecondary, fontSize: '11px', textAlign: 'right' }}>{index + 2}</div>
            <div style={{ flex: '1', minWidth: 220, padding: '4px 8px', borderRadius: '3px', backgroundColor: 'transparent', color: getColorForLine(line) }}>{line}</div>
          </div>
        ))}

        <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: `1px solid ${Colors.light.border}`, fontSize: '11px', color: Colors.light.textSecondary }}>
          <div>Perkiraan baris: ~10-30</div>
          <div>Kompleksitas: O(n^2) sampai O(n log n)</div>
          <div>Cocok untuk: {getBestUse()}</div>
        </div>
      </div>

      <div style={{ marginTop: '20px' }}>
        <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', color: Colors.light.text }}>Catatan pilihan</h4>
        <div style={{ padding: '10px', backgroundColor: Colors.light.background, borderRadius: '4px', fontSize: '13px', color: Colors.light.textSecondary, lineHeight: '1.6' }}>
          {algorithm.name === 'Bubble Sort' && 'Bubble Sort mudah dipahami, tetapi lambat untuk data besar. Cocok untuk belajar konsep awal.'}
          {algorithm.name === 'Selection Sort' && 'Selection Sort mengurangi jumlah pertukaran, tetapi tetap memindai data penuh.'}
          {algorithm.name === 'Insertion Sort' && 'Insertion Sort bagus untuk data kecil atau hampir urut, tetapi lambat untuk data acak yang besar.'}
          {algorithm.name === 'Merge Sort' && 'Merge Sort stabil dan konsisten, tetapi membutuhkan memori tambahan.'}
          {algorithm.name === 'Quick Sort' && 'Quick Sort cepat secara rata-rata, tetapi bisa lambat jika pembagian pivot buruk.'}
          {algorithm.name === 'Heap Sort' && 'Heap Sort konsisten dan hemat memori, tetapi tidak stabil untuk nilai yang sama.'}
          {algorithm.name === 'Shell Sort' && 'Shell Sort mempercepat Insertion Sort dengan jarak pembanding yang makin kecil.'}
        </div>
      </div>
    </div>
  );
}
